"use client";

import React, { useState } from "react";
import { 
  X, 
  Dumbbell, 
  CheckCircle2, 
  Clock, 
  Sparkles, 
  Calendar, 
  ShieldCheck, 
  AlertCircle 
} from "lucide-react";
import { FITNESS_GOALS, TIME_PREFERENCES } from "@/lib/demos/gymData";

interface TrialPassModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency?: "INR" | "USD";
  preselectedTier?: string;
}

export default function TrialPassModal({
  isOpen,
  onClose,
  currency = "INR",
  preselectedTier = "Executive Performance",
}: TrialPassModalProps) {
  const [formData, setFormData] = useState({
    lead_name: "",
    email: "",
    phone: "",
    fitness_goal: FITNESS_GOALS[0],
    membership_tier: preselectedTier,
    preferred_time: TIME_PREFERENCES[0],
    fitness_level: "Intermediate (Regular strength training)",
    notes: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/demos/gym/members", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          currency,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || "Failed to submit trial pass application.");
      }
    } catch (err: any) {
      setError(err.message || "Network error submitting application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-stone-950 border border-stone-800 rounded-3xl max-w-xl w-full p-6 sm:p-8 relative shadow-2xl overflow-y-auto max-h-[90vh] text-stone-100">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-stone-400 hover:text-white p-2 rounded-full hover:bg-stone-900 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-8 space-y-4 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto text-amber-400">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-stone-900 border border-stone-800 text-[11px] font-bold text-amber-400 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Biometric Pass Registered</span>
            </div>

            <h3 className="text-2xl font-black text-white tracking-tight">
              VIP 3-Day Guest Pass Requested
            </h3>

            <p className="text-xs text-stone-300 max-w-md mx-auto leading-relaxed">
              Thank you, <strong>{formData.lead_name}</strong>. An orientation confirmation has been dispatched to <strong>{formData.email}</strong>.
            </p>

            <div className="p-4 rounded-2xl bg-stone-900/80 border border-stone-800 text-left text-xs space-y-2 max-w-md mx-auto text-stone-300">
              <div className="flex justify-between border-b border-stone-800 pb-2">
                <span className="text-stone-400">Primary Goal:</span>
                <span className="font-semibold text-white">{formData.fitness_goal}</span>
              </div>
              <div className="flex justify-between border-b border-stone-800 pb-2">
                <span className="text-stone-400">Target Tier:</span>
                <span className="font-semibold text-amber-400">{formData.membership_tier}</span>
              </div>
              <div className="flex justify-between pt-1">
                <span className="text-stone-400">Assigned Facility:</span>
                <span className="font-semibold text-emerald-400">Olympic Bays &amp; InBody Lab</span>
              </div>
            </div>

            <div className="pt-4 flex justify-center">
              <button
                onClick={onClose}
                className="py-2.5 px-8 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 font-extrabold text-xs transition-colors shadow-lg shadow-amber-500/20"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">
                  <Dumbbell className="w-4 h-4" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-black text-white">
                    Claim VIP 3-Day Guest Pass
                  </h2>
                  <p className="text-xs text-stone-400">
                    Complimentary Olympic floor access, InBody 770 scan, and coach orientation.
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Marcus Aurelius"
                  value={formData.lead_name}
                  onChange={(e) => setFormData({ ...formData, lead_name: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-stone-600 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Email Address (For Pass PDF) *
                </label>
                <input
                  type="email"
                  required
                  placeholder="name@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-stone-600 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Mobile / WhatsApp (Optional)
                </label>
                <input
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-stone-600 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Experience Level *
                </label>
                <select
                  value={formData.fitness_level}
                  onChange={(e) => setFormData({ ...formData, fitness_level: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="Beginner (Starting new routine)">Beginner (Starting new routine)</option>
                  <option value="Intermediate (Regular strength training)">Intermediate (Regular strength training)</option>
                  <option value="Advanced (Powerlifting / Hyrox / Athlete)">Advanced (Powerlifting / Hyrox / Athlete)</option>
                  <option value="Post-Rehab (Spine / Joint Focus)">Post-Rehab (Spine / Joint Focus)</option>
                </select>
              </div>

              <div className="sm:col-span-2 space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Primary Athletic Goal *
                </label>
                <select
                  value={formData.fitness_goal}
                  onChange={(e) => setFormData({ ...formData, fitness_goal: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  {FITNESS_GOALS.map((goal) => (
                    <option key={goal} value={goal}>
                      {goal}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Target Membership Tier
                </label>
                <select
                  value={formData.membership_tier}
                  onChange={(e) => setFormData({ ...formData, membership_tier: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="Apex Black Card (All-Access)">Apex Black Card (All-Access)</option>
                  <option value="Executive Performance">Executive Performance</option>
                  <option value="Strength & Conditioning">Strength & Conditioning</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Preferred Training Hours
                </label>
                <select
                  value={formData.preferred_time}
                  onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  {TIME_PREFERENCES.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2 space-y-1">
                <label className="text-[11px] font-bold text-stone-300 uppercase tracking-wider">
                  Injuries, Medical Notes or Coach Preferences (Optional)
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Lumbar disc history, looking for Hyrox competition pacing, preference for morning 7 AM coach."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-xs text-white placeholder:text-stone-600 focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20 text-[11px] text-amber-300/90 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
              <span>Complimentary Eleiko chalk, towel service &amp; infrared sauna access included with pass.</span>
            </div>

            <div className="pt-2 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-xs font-semibold text-stone-400 hover:text-white transition-colors"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-stone-950 font-bold text-xs transition-all shadow-md shadow-amber-500/20 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? "Registering Pass..." : "Claim 3-Day VIP Pass"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
